import { ClassValue, clsx } from 'clsx';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { origin } from '~/helpers/projectConstant';
import { ProviderSummary } from '~/types/Provider';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getParamsFromUrl(search: string) {
  const searchParams = new URLSearchParams(search);
  const params: { [key: string]: string | undefined } = {};

  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
}

export function convertThousandsStringToInt(numberString: string) {
  return parseInt(numberString.replace('.', ''), 10);
}

export function splitArrayIntoMultiple(arr: any[], n?: number) {
  if (!n) n = 2;
  const splitSize = Math.ceil(arr.length / n);
  const result = [];

  for (let i = 0; i < n; i++) {
    const start = i * splitSize;
    const end = start + splitSize;
    result.push(arr.slice(start, end));
  }

  return result;
}

export function stripBracketTagsFromContent(content: string): string {
  content = content.replace(/\[\w]/g, '');
  content = content.replace(/\[\/\w]/g, '');
  return content;
}

export function addHttpsIfMissing(url: string): string {
  if (!url.startsWith('https://') && !url.startsWith('http://')) {
    return 'https://' + url;
  }
  return url;
}

export const cdnUrl: string = 'https://cdn.advieskeuze.nl/content';

export function formatPrice(value?: number): string {
  if (!value) {
    return '';
  }

  const formatted = new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  return formatted.includes(',00') ? formatted.replace(',00', ',-') : formatted;
}

export function removeDutchTelFormatting(tel: string): string {
  if (tel.startsWith('+31')) {
    return '0' + tel.replace('+31', '');
  }

  return tel;
}

export function addDutchTelFormattingWithPlus(tel: string): string {
  return '+' + addDutchTelFormatting(tel);
}

export function addDutchTelFormatting(tel: string): string {
  if (tel.startsWith('0')) {
    return '31' + tel.slice(1);
  }

  return tel;
}

export function formatDutchTelWithHyphen(phone: string): string {
  return phone.slice(0, 2) + '-' + phone.slice(2);
}

export function replaceWithA(text: string) {
  const inlineStyle = 'color: #2399EE; text-decoration: underline;';
  let modifiedText = text.replace(
    '[url= _blank]',
    `<a target="_blank" href=${`${origin}/home/privacy`} style="${inlineStyle}">`
  );
  modifiedText = modifiedText.replace('[/url]', '</a>');
  return modifiedText;
}

export function groupByFirstLetter<T extends { name: string }>(items: T[]) {
  return items.reduce<Record<string, T[]>>((groups, item) => {
    const letter = item.name.charAt(0).toUpperCase() || '#';
    (groups[letter] ||= []).push(item);
    return groups;
  }, {});
}

export function filterCollectionBySearch<T extends { name: string }>(
  collection: T[] | null | undefined,
  search: string
): T[] {
  const list = Array.isArray(collection) ? collection : [];

  if (!search) return list.sort((a, b) => a.name.localeCompare(b.name));

  return list
    .filter(({ name }) => name.toLowerCase().startsWith(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function filterProvidersBySelectedScopes(
  provider: ProviderSummary,
  selectedScopes: string[]
): boolean {
  const providerScopeSlugs = provider.scopes.map((s) => s.slug);
  return selectedScopes.every((slug) => providerScopeSlugs.includes(slug));
}

export function getChildByDisplayName<T extends React.ReactElement>(
  children: React.ReactNode,
  displayName?: string
): T | undefined {
  return React.Children.toArray(children).find((child): child is T => {
    return (
      React.isValidElement(child) &&
      typeof child.type !== 'string' &&
      (child.type as any).displayName === displayName
    );
  });
}

export function serializeErrors(errors: any) {
  const result: Record<string, any> = {};
  for (const key in errors) {
    if (errors[key]) {
      result[key] = { message: errors[key].message };
    }
  }
  return result;
}

export function formatNumberWithDots(
  value: number | null | undefined,
  minDigits: number = 0,
  maxDigits: number = 2
): string {
  value = !value ? 0 : value;

  return new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  }).format(value);
}

export function parseFormattedNumber(input: string): number {
  const normalized = input.replace(/\./g, '').replace(',', '.');
  return parseFloat(normalized);
}